/**
 * Tracking — admin
 *
 * The screen exists because confirming a shipment to a retailer takes TWO Mirakl calls:
 * OR23 submits the tracking number, OR24 validates the shipment. They fail independently,
 * so every view here reports them separately. An order that passed OR23 and failed OR24
 * reads as shipped to us and unshipped to the retailer — and on a PAY_ON_SHIPMENT
 * workflow, that means unpaid. A single "tracking sent" state would hide it.
 */

import React, { useEffect, useState } from 'react';
import {
  Page, Layout, Card, Text, Box, BlockStack, InlineStack, InlineGrid, Badge, Button,
  // EmptyState and Divider are deliberately absent: every section's empty state is now a
  // row inside its own table, so the headings stay visible, and nothing on the page divides
  // by rule any more.
  IndexTable, Spinner, Banner, Select, Tooltip, Pagination,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useTrackingSummary, useTrackingJobs, useRunTrackingAction } from '../utils/queries';
import { headingsFor, emptyRow, DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';

/** Which Mirakl call each queue's button actually makes. */
const QUEUE_ACTIONS = {
  'Validate shipment': 'validate',
  'Push tracking': 'push',
  // The client's own name for this on the Aug 5 call. It reads Shopify again for a
  // tracking number and pushes whatever it finds, so "update" is what it does from
  // the admin's side — the OR-number labels above describe the Mirakl call instead,
  // which is the right frame for those two because the queue is defined by them.
  'Update Tracking': 'recheck',
};

const PERIODS = [
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
];

const when = (d) => (d ? new Date(d).toLocaleString() : '—');
const hrs = (h) => (h == null ? '—' : h < 24 ? `${h}h` : `${Math.floor(h / 24)}d ${h % 24}h`);

/**
 * Rows-per-page and page number for ONE table.
 *
 * Every table on this screen gets its own. They are read separately — the three queues are
 * worked through one at a time, and Push failures is scanned rather than read — so a single
 * shared size would resize five tables because someone widened one. Called once per table
 * below; nothing is persisted, so a reload starts at the default.
 *
 * The reset is QueueTable's, for the same reason: a new row count, or a new PERIOD (the only
 * filter on this screen), starts again at page 1. Without it, narrowing the period while on
 * page 4 renders an empty slice, which reads as "nothing waiting" rather than "wrong page".
 */
const usePaging = (seed, resetKey) => {
  const [pageSize, setPageSize] = useState(seed);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [pageSize, resetKey]);
  return { page, setPage, pageSize, setPageSize };
};

/**
 * The slice a table actually renders, with the page CLAMPED to what exists.
 *
 * Everything here is paged CLIENT-SIDE: GET /tracking/summary sends only `days` and returns
 * the queues, the SLA rows and the failures whole, so the page is cut from the array already
 * in hand and no size change costs a request. Clamping is not optional — this screen refetches
 * (period change, and every run of a per-row action invalidates the query), so a queue can
 * drain under a page that was valid when it was chosen, and an out-of-range slice would put
 * the "nothing waiting" row over a table that still has orders in it.
 */
const windowOf = (rows, { page, pageSize }) => {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  return {
    totalPages,
    safePage,
    visible: rows.slice((safePage - 1) * pageSize, safePage * pageSize),
  };
};

/**
 * Both controls in one row under a table, and only once it has rows — a pager under an empty
 * table is three dead words. The pager drops out when everything fits; the row count stays,
 * because it is how someone gets more than one page in the first place.
 */
const tableFooter = (total, paging, win) => (total === 0 ? null : (
  <Box padding="300">
    <InlineStack align="space-between" blockAlign="center">
      <PageSizeSelect value={paging.pageSize} onChange={paging.setPageSize} />
      {win.totalPages > 1 && (
        /* Labelled with `safePage`, the page the rows were actually sliced with — the raw
           value would read "Page 4 of 2" for as long as a drained queue stayed drained. */
        <Pagination
          hasPrevious={win.safePage > 1}
          onPrevious={() => paging.setPage(win.safePage - 1)}
          hasNext={win.safePage < win.totalPages}
          onNext={() => paging.setPage(win.safePage + 1)}
          label={`Page ${win.safePage} of ${win.totalPages}`}
        />
      )}
    </InlineStack>
  </Box>
));

// Mirrors the server-side `take: 25` on the tracking-push failures query
// (tracking.controller.js:72-78). Used only to label the list as capped, never as a total.
const FAILURES_PAGE_CAP = 25;

/**
 * Column definitions, one place per table.
 *
 * Two things this file previously did not do, both asked for after the screen was reviewed
 * with nothing in it:
 *
 *  1. HEADINGS MUST SURVIVE AN EMPTY TABLE. Every section here rendered a bare sentence in
 *     a Box when its list was empty, so the screen read as six paragraphs with no structure
 *     and no way to tell what each section would eventually contain. IndexTable cannot do
 *     this on its own: at itemCount 0 it replaces the WHOLE table with emptyState, headings
 *     included. The pattern that works — already used by SkuMapTable and QueueTable — is
 *     itemCount={rows.length || 1} plus one synthetic row whose single cell spans every
 *     column. `tableFor` below does that, so no section can forget it.
 *
 *  2. EVERY COLUMN EXPLAINS ITSELF. `tip` becomes IndexTable's own tooltipContent. These are
 *     not decoration: several of these columns cannot be read correctly without them —
 *     "Shipped" is not a carrier scan, "Carrier" is not the code Mirakl was sent, "Step" is
 *     inferred from error text rather than reported, and "Orders" is structurally always 0
 *     for a tracking job. A column that needs a caveat gets one here rather than in a
 *     comment nobody reading the screen can see.
 */
const QUEUE_COLUMNS = [
  { key: 'order', title: 'Mirakl ID', tip: "The order id the retailer gave it. The smaller number below is the Shopify order we created for it." },
  { key: 'connection', title: 'Connection', tip: 'Which brand, on which retailer account. Opens that brand.' },
  { key: 'tracking', title: 'Tracking', tip: "The tracking number as it stands in Shopify. Whether the retailer has it is what the queue you are looking at tells you — this column does not." },
  { key: 'carrier', title: 'Carrier', tip: "The carrier as Shopify names it. Mirakl is sent BOTH this text and a carrier code — resolved from that retailer's own carrier list, or, before that list has been fetched for the first time, guessed from a built-in table." },
  { key: 'shipped', title: 'Shipped', tip: 'When the fulfilment was recorded. Not a carrier scan and not a delivery — Mirakl reports no delivery confirmation to us at all.' },
  { key: 'waiting', title: 'Waiting', tip: 'How long since the fulfilment was recorded. Amber past 4 hours, red past 24.' },
  { key: 'actions', title: 'Actions', tip: 'Runs the Mirakl call for this one order immediately, rather than waiting for the schedule. It is refused if tracking writes are switched off in Sync Settings.' },
];

const SLA_COLUMNS = [
  { key: 'retailer', title: 'Retailer', tip: 'The retailer account, named as it was set up in 2W.' },
  { key: 'orders', title: 'Orders', tip: "Orders in this period that carry the retailer's placed-at time. Orders without it cannot be measured and are excluded — the coverage badge above says how many that is." },
  { key: 'w24', title: 'Shipped ≤24h', tip: 'Shipped within 24 hours of the retailer placing the order.' },
  { key: 'w48', title: 'Shipped ≤48h', tip: 'Shipped within 48 hours. This INCLUDES the ≤24h column — it is a running total, not a separate band.' },
  { key: 'u24', title: 'Unshipped >24h', tip: 'Still unshipped between 24 and 48 hours after the order was placed. This one IS a separate band, so it does not overlap the column beside it.' },
  { key: 'u48', title: 'Unshipped >48h', tip: 'Still unshipped more than 48 hours after the order was placed.' },
  { key: 'ontime', title: 'On time', tip: 'Shipped within 24 hours, as a share of orders that have either shipped or already passed 24 hours. Orders younger than 24 hours and still unshipped are excluded rather than counted as failures. This is measured against 24 hours, not against the retailer’s own ship-by deadline.' },
];

const FAILURE_COLUMNS = [
  { key: 'connection', title: 'Connection', tip: 'Which brand, on which retailer account.' },
  { key: 'step', title: 'Step', tip: 'Inferred from the wording of the error, not reported by the retailer. Treat it as a hint about which Mirakl call was involved rather than as a fact.' },
  { key: 'error', title: 'Error', tip: 'The message from the job that failed. A refusal of one individual order is recorded on that order instead, so it will not appear here.' },
  { key: 'when', title: 'When', tip: 'When the job was queued, which is slightly before it failed.' },
];

const JOB_COLUMNS = [
  { key: 'connection', title: 'Connection', tip: 'Which brand, on which retailer account.' },
  { key: 'status', title: 'Status', tip: 'Completed means the job finished, not that every order in it succeeded — an order the retailer refused is recorded on the order, and the job still completes.' },
  // No "Orders" column, deliberately. Do not add one back without changing the writer
  // first: _handleTrackingPush passes only { trackingPushed } to completeSyncJob
  // (mirakl-sync-worker.js), and completeSyncJob defaults ordersProcessed to 0
  // (mirakl-sync-job-service.js:39). So the column could only ever render 0, and read as
  // "this job saw no orders" — a number that can only mislead. "Pushed" below is the figure
  // that carries information. The API still returns `orders`; nothing reads it now.
  { key: 'pushed', title: 'Pushed', tip: 'Tracking numbers this run got accepted by a retailer. This is the figure to read — a tracking job records no order count of its own.' },
  { key: 'duration', title: 'Duration', tip: 'Wall clock for the whole run, including any time spent waiting on Shopify or on a lock.' },
  { key: 'when', title: 'When', tip: 'When the job was queued.' },
];

// headingsFor now lives in utils/table.jsx — one copy for the whole console.

export default function Tracking() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30');
  const { data, isLoading, error } = useTrackingSummary({ days: period });

  /**
   * The jobs table is the one SERVER-PAGED list on this screen: GET /tracking/jobs takes a
   * `limit` (capped at 100 server-side) and the chosen size IS that limit, so the size lands
   * in the query key and the request re-fires — slicing the array instead would show 10 of the
   * 50 rows the server had already sent. The endpoint takes no offset or page, so there is no
   * page 2 to reach: the row count is the whole control, and its footer carries no pager.
   * Seeded at 10, the value this screen already asked for.
   */
  const [jobsPageSize, setJobsPageSize] = useState(10);
  const { data: jobsData } = useTrackingJobs({ limit: jobsPageSize });
  const runAction = useRunTrackingAction();

  // One per table, so no two move together. Every one resets on a period change.
  const validationPaging = usePaging(DEFAULT_PAGE_SIZE, period);
  const pushPaging = usePaging(DEFAULT_PAGE_SIZE, period);
  const missingPaging = usePaging(DEFAULT_PAGE_SIZE, period);
  const slaPaging = usePaging(DEFAULT_PAGE_SIZE, period);
  const failurePaging = usePaging(DEFAULT_PAGE_SIZE, period);

  // Per-row, so only the clicked button spins. `result` is keyed by row too: an outcome
  // belongs to the order it came from, and a shared banner would attribute the last
  // answer to whichever row the eye lands on.
  const [pending, setPending] = useState(null);
  const [results, setResults] = useState({});

  const onAction = async (rowId, actionLabel) => {
    const action = QUEUE_ACTIONS[actionLabel];
    if (!action) return;
    setPending(rowId);
    setResults((r) => ({ ...r, [rowId]: null }));
    try {
      const res = await runAction.mutateAsync({ orderRowId: rowId, action });
      setResults((r) => ({ ...r, [rowId]: res }));
    } catch (err) {
      // Only a transport or 5xx failure lands here — Mirakl refusals arrive as 200.
      setResults((r) => ({ ...r, [rowId]: { ok: false, message: err?.message || 'Request failed' } }));
    } finally {
      setPending(null);
    }
  };

  const p = data?.pipeline ?? {};
  const q = data?.queues ?? {};
  const sla = data?.sla ?? [];
  const failures = data?.failures ?? [];
  const jobs = jobsData?.jobs ?? [];

  // The queues get their window inside `queueTable`; these two are rendered inline, so the
  // slice is taken here. `jobs` gets none: the server already returned exactly one page of it.
  const slaWin = windowOf(sla, slaPaging);
  const failureWin = windowOf(failures, failurePaging);

  if (error) {
    return (
      <Page fullWidth>
        <Layout><Layout.Section>
          <Banner tone="critical">Unable to load tracking data: {error.message}</Banner>
        </Layout.Section></Layout>
      </Page>
    );
  }

  const stat = (label, value, tone, sub, tip) => (
    <Card>
      <Box padding="300">
        <BlockStack gap="050">
          <InlineStack gap="100" blockAlign="center">
            <Text variant="bodySm" tone="subdued">{label}</Text>
            {tip && <Tooltip content={tip}><Text variant="bodySm" tone="subdued">ⓘ</Text></Tooltip>}
          </InlineStack>
          <Text variant="heading2xl" tone={tone}>{isLoading ? '—' : value ?? 0}</Text>
          {sub && <Text variant="bodySm" tone="subdued">{sub}</Text>}
        </BlockStack>
      </Box>
    </Card>
  );

  /**
   * One table, headings always present.
   *
   * `count` exists separately from `rows.length` because IndexTable needs a non-zero
   * itemCount to render its header row at all — so the synthetic empty row is counted as
   * one item and the real emptiness is expressed by the row itself, not by the count.
   */
  const tableFor = (cols, rows, resourceName, emptyMsg, children) => (
    <IndexTable
      resourceName={resourceName}
      itemCount={rows.length || 1}
      selectable={false}
      loading={isLoading}
      headings={headingsFor(cols)}
    >
      {/* Never "everything is fine" when the request failed — an error and an empty period
          are different statements and must not share copy. */}
      {rows.length === 0
        ? emptyRow(cols, `${cols[0].key}-${resourceName.plural}`,
          error ? 'Could not load this list — see the error above.' : emptyMsg)
        : children}
    </IndexTable>
  );

  /**
   * `paging` is per call site on purpose. Each queue is its own working list, so widening
   * "Awaiting validation" must not resize the two queues below it.
   *
   * The badge counts `rows`, the whole queue, while the table renders `win.visible` — the
   * count is the size of the problem, not the size of the window onto it.
   */
  const queueTable = (rows, title, emptyMsg, actionLabel, tone, paging) => {
    const win = windowOf(rows, paging);
    return (
    <Card>
      <Box padding="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd" as="h3">{title}</Text>
          {rows.length > 0 && <Badge tone={tone}>{`${rows.length} order${rows.length === 1 ? '' : 's'}`}</Badge>}
        </InlineStack>
      </Box>
      {tableFor(
        QUEUE_COLUMNS, win.visible, { singular: 'order', plural: 'orders' }, emptyMsg,
        win.visible.map((o, i) => (
            <IndexTable.Row id={o.id} key={o.id} position={i}>
              <IndexTable.Cell>
                <Text variant="bodySm" fontWeight="semibold">{o.miraklOrderId}</Text>
                {o.shopifyOrderId && <Text variant="bodySm" tone="subdued">#{o.shopifyOrderId}</Text>}
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Button variant="plain" onClick={() => o.brandId && navigate(`/brands/${o.brandId}`)}>
                  {o.brandName} → {o.retailer}
                </Button>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm">{o.trackingNumber || <Text tone="subdued" as="span">none</Text>}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell><Text variant="bodySm">{o.carrier || '—'}</Text></IndexTable.Cell>
              <IndexTable.Cell><Text variant="bodySm">{when(o.shippedAt)}</Text></IndexTable.Cell>
              <IndexTable.Cell>
                <Badge tone={o.waitingHours > 24 ? 'critical' : o.waitingHours > 4 ? 'warning' : undefined}>
                  {hrs(o.waitingHours)}
                </Badge>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <BlockStack gap="100">
                  <Button
                    size="micro"
                    loading={pending === o.id}
                    disabled={pending !== null && pending !== o.id}
                    onClick={() => onAction(o.id, actionLabel)}
                  >
                    {actionLabel}
                  </Button>
                  {results[o.id] && (
                    <Text
                      variant="bodySm"
                      tone={results[o.id].ok ? 'success' : 'critical'}
                    >
                      {results[o.id].message}
                    </Text>
                  )}
                </BlockStack>
              </IndexTable.Cell>
            </IndexTable.Row>
        )),
      )}
      {tableFooter(rows.length, paging, win)}
    </Card>
    );
  };

  return (
    <Page fullWidth>
      <Layout>
        <Layout.Section>
          <InlineStack align="space-between" blockAlign="center" gap="400" wrap>
            <BlockStack gap="050">
              <Text variant="headingLg" as="h1">Tracking</Text>
              {/* "Retailer orders only" is the load-bearing half. Every figure on this page
                  counts connector orders, so a brand selling direct reads zero throughout —
                  which is correct and looks broken unless the page says so. */}
              <Text variant="bodySm" tone="subdued">
                Shopify fulfillments pushed out to retailers &middot; retailer orders only
              </Text>
            </BlockStack>
            <InlineStack gap="200" blockAlign="center">
              {isLoading && <Spinner size="small" />}
              <Select label="Period" labelHidden options={PERIODS} value={period} onChange={setPeriod} />
            </InlineStack>
          </InlineStack>
        </Layout.Section>

        {/* Two calls, reported separately */}
        <Layout.Section>
          <Banner tone="info">
            <Text variant="bodySm">
              Confirming a shipment takes two separate steps — the <strong>tracking push</strong> sends the
              tracking number to the retailer, then <strong>shipment validation</strong> confirms the shipment
              with them. They fail independently, so an order can be
              pushed and never validated. That gap is the <strong>Awaiting validation</strong> queue below, and it
              is the one that leaves a seller unpaid on pay-on-shipment terms.
            </Text>
          </Banner>
        </Layout.Section>

        <Layout.Section>
          <InlineGrid gap="400" columns={{ xs: 1, sm: 2, md: 4, lg: 4, xl: 4 }}>
            {/* Named for what it counts. It said "Fulfilled in Shopify" and counted retailer
                orders, so on a store selling direct it read 0 while four orders had in fact
                been fulfilled in Shopify. It is also the first step of the funnel below, and
                only reads as one while its denominator stays retailer orders. */}
            {stat('Retailer orders fulfilled', p.fulfilled, undefined, 'in this period',
              'Retailer orders fulfilled in Shopify in this period. Orders placed directly in the store are not counted: there is no retailer to push tracking to.')}
            {stat('Tracking pushed', p.trackingPushed, 'success', 'tracking accepted by the retailer')}
            {stat('Shipment validated', p.shipValidated, p.pushedNotValidated > 0 ? 'warning' : 'success', 'shipment confirmed by the retailer')}
            {stat('Pushed, not validated', p.pushedNotValidated, p.pushedNotValidated > 0 ? 'critical' : 'success',
              'shipped to us, not to them',
              'These orders show a tracking number on the retailer side but the retailer does not consider them shipped. On pay-on-shipment terms they are unpaid.')}
          </InlineGrid>
        </Layout.Section>

        {/* Second row: what the queues hold right now, and whether the pipeline moved
            in the last day. Deliberately a 24h window rather than the selected period —
            a 90-day total cannot answer "is this working today". */}
        <Layout.Section>
          <InlineGrid gap="400" columns={{ xs: 1, sm: 2, md: 4, lg: 4, xl: 4 }}>
            {stat('Awaiting tracking push', (q.awaitingPush ?? []).length,
              (q.awaitingPush ?? []).length > 0 ? 'caution' : undefined,
              'number ready, retailer not told',
              'Fulfilled with a tracking number that has not reached the retailer yet.')}
            {stat('Missing tracking', (q.missingTracking ?? []).length,
              (q.missingTracking ?? []).length > 0 ? 'caution' : undefined,
              'retailer orders, fulfilled with no number',
              'Retailer orders fulfilled in Shopify with no tracking number to send. The Dashboard counts this across every order, retailer or not, so the two differ on a brand selling direct.')}
            {stat('Pushed (24h)', data?.last24h?.pushed, undefined, 'tracking pushed in the last day',
              'Tracking numbers accepted by a retailer in the last 24 hours — how you tell the pipeline is moving.')}
            {stat('Push failures (24h)', data?.last24h?.failures,
              (data?.last24h?.failures ?? 0) > 0 ? 'critical' : undefined,
              p.carrierRejected > 0
                ? `${p.carrierRejected} from a rejected carrier code`
                : 'none from carrier codes',
              'Failed tracking-push jobs in the last 24 hours. The sub-line counts how many were rejected because the carrier code was not one the retailer accepts.')}
          </InlineGrid>
        </Layout.Section>

        {/* SLA by retailer */}
        <Layout.Section>
          <Card>
            <Box padding="300">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="050">
                  <Text variant="headingMd" as="h3">Shipping SLA by retailer</Text>
                  <Text variant="bodySm" tone="subdued">
                    Measured from when the retailer placed the order, not when we imported it.
                  </Text>
                </BlockStack>
                {data && !data.slaReliable && (
                  <Tooltip content={`Only ${data.slaCoverage}% of orders carry the retailer's placed-at time, so these figures are not yet reliable.`}>
                    <Badge tone="warning">{`${data.slaCoverage}% coverage`}</Badge>
                  </Tooltip>
                )}
              </InlineStack>
            </Box>
            {tableFor(
              SLA_COLUMNS, slaWin.visible, { singular: 'retailer', plural: 'retailers' },
              "No orders in this period carry the retailer's placed-at time, so there is nothing to measure against.",
              slaWin.visible.map((r, i) => (
                  <IndexTable.Row id={r.marketplaceId ?? String(i)} key={r.marketplaceId ?? i} position={i}>
                    <IndexTable.Cell><Text variant="bodySm" fontWeight="semibold">{r.retailer}</Text></IndexTable.Cell>
                    <IndexTable.Cell><Text variant="bodySm">{r.orders}</Text></IndexTable.Cell>
                    <IndexTable.Cell><Text variant="bodySm">{r.within24}</Text></IndexTable.Cell>
                    <IndexTable.Cell><Text variant="bodySm">{r.within48}</Text></IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm" tone={r.unshipped24 > 0 ? 'caution' : undefined}>{r.unshipped24}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm" tone={r.unshipped48 > 0 ? 'critical' : undefined}>{r.unshipped48}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm" tone={r.onTimeRate >= 90 ? 'success' : r.onTimeRate >= 75 ? 'caution' : 'critical'}>
                        {r.onTimeRate == null ? '—' : `${r.onTimeRate}%`}
                      </Text>
                    </IndexTable.Cell>
                  </IndexTable.Row>
              )),
            )}
            {tableFooter(sla.length, slaPaging, slaWin)}
          </Card>
        </Layout.Section>

        {/* Three queues — deliberately not merged */}
        <Layout.Section>
          {queueTable(q.awaitingValidation ?? [], 'Awaiting validation — tracking sent, shipment not confirmed',
            'Nothing waiting in this period. An order pushed before it is not checked here, so this is not a statement about every order ever pushed.', 'Validate shipment', 'critical', validationPaging)}
        </Layout.Section>
        <Layout.Section>
          {queueTable(q.awaitingPush ?? [], 'Awaiting tracking push — number ready, retailer not told',
            'Nothing waiting in this period. An order fulfilled before it is not checked here — which is exactly the order most worth finding, so widen the period before concluding nothing is stuck.', 'Push tracking', 'warning', pushPaging)}
        </Layout.Section>
        <Layout.Section>
          {queueTable(q.missingTracking ?? [], 'Missing tracking — fulfilled with no number',
            'No fulfilled order in this period is missing a tracking number. Note that a fulfilment recorded WITHOUT one writes no shipped date either, so such an order may not appear here at all.', 'Update Tracking', 'warning', missingPaging)}
        </Layout.Section>

        {/* Failures */}
        <Layout.Section>
          <Card>
            <Box padding="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingMd" as="h3">Push failures</Text>
                {/* `failures` is the failedJobs query capped at `take: 25`
                    (tracking.controller.js:72-78), and GET /tracking/summary returns no
                    period-wide failure total - only last24h.failures. So the badge cannot state
                    the true count; it must not imply that 25 IS the count either. Show it as
                    capped once the list is full.
                    BACKEND FOLLOW-UP: add a `failuresTotal` (an unbounded count on the same
                    where-clause) to the summary response and this becomes exact. Note the same
                    cap also feeds pipeline.carrierRejected, which is therefore understated. */}
                {failures.length > 0 && (
                  <Badge tone="critical">
                    {failures.length >= FAILURES_PAGE_CAP ? `${FAILURES_PAGE_CAP}+` : `${failures.length}`}
                  </Badge>
                )}
              </InlineStack>
            </Box>
            {tableFor(
              FAILURE_COLUMNS, failureWin.visible, { singular: 'failure', plural: 'failures' },
              // Precise about what an empty list does and does not prove: a job aborting is
              // what lands here, and an individual order refused by a retailer does not.
              'No tracking-push job failed in this period. An order the retailer refused individually is recorded on that order, not here.',
              failureWin.visible.map((f, i) => (
                  <IndexTable.Row id={f.id} key={f.id} position={i}>
                    <IndexTable.Cell>
                      <Button variant="plain" onClick={() => f.brandId && navigate(`/brands/${f.brandId}`)}>
                        {f.brandName} → {f.retailer}
                      </Button>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={f.step.startsWith('OR24') ? 'warning' : undefined}>{f.step}</Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell><Text variant="bodySm" tone="critical">{f.error}</Text></IndexTable.Cell>
                    <IndexTable.Cell><Text variant="bodySm">{when(f.at)}</Text></IndexTable.Cell>
                  </IndexTable.Row>
              )),
            )}
            {/* The badge above still counts what the server sent (capped at 25); this pages
                that list. Both numbers are about the same array, so they cannot disagree. */}
            {tableFooter(failures.length, failurePaging, failureWin)}
          </Card>
        </Layout.Section>

        {/* Recent jobs */}
        <Layout.Section>
          <Card>
            <Box padding="300"><Text variant="headingMd" as="h3">Recent tracking push jobs</Text></Box>
            {tableFor(
              JOB_COLUMNS, jobs, { singular: 'job', plural: 'jobs' },
              'No tracking-push job has run yet. This list ignores the period selector above, so it is empty because none has ever run rather than because none ran recently.',
              jobs.map((j, i) => (
                  <IndexTable.Row id={j.id} key={j.id} position={i}>
                    <IndexTable.Cell>
                      <Text variant="bodySm">{j.brandName} → {j.retailer}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={j.status === 'FAILED' ? 'critical' : j.status === 'COMPLETED' ? 'success' : undefined}>
                        {j.status}
                      </Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell><Text variant="bodySm">{j.trackingPushed}</Text></IndexTable.Cell>
                    <IndexTable.Cell><Text variant="bodySm">{j.duration ? `${(j.duration / 1000).toFixed(1)}s` : '—'}</Text></IndexTable.Cell>
                    <IndexTable.Cell><Text variant="bodySm">{when(j.at)}</Text></IndexTable.Cell>
                  </IndexTable.Row>
              )),
            )}
            {/* Rows-per-page only, no pager: the size IS the API's `limit` and /tracking/jobs
                takes no offset, so there is nothing after the first page to turn to. Changing
                it re-requests — the value is part of the query key. */}
            {jobs.length > 0 && (
              <Box padding="300">
                <InlineStack align="space-between" blockAlign="center">
                  <PageSizeSelect value={jobsPageSize} onChange={setJobsPageSize} />
                </InlineStack>
              </Box>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
